import { t } from 'i18next';
import moment from 'moment';
import * as Yup from 'yup';
export const emailREGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]+$/;
export const passwordREGEX = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
export const mobileNumberREGEX = /^\d{8,12}$/;
export const mobileNumberLength = 10;
// 10 digits plus the two dashes a formatted number carries (231-313-9966)
export const formattedMobileNumberLength = 12;
// shows the grouping a phone field expects before anything has been typed
export const mobileNumberPlaceholder = 'XXX-XXX-XXXX';
export const postalCodeREGEX = /^.{6,7}$/;
export const websiteREGEX = /^(https?:\/\/)?([\w\d-]+\.)*[\w\d-]+\.[\w\d]{2,}(\/.*)?$/;
export const onlyCharacterREGEX = /^[a-zA-Z\s]*$/;
export const urlREGEX = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/;
export const FFLNumberPatternFormat = '#-##-###-##-##-#####';
export const FFLNumberPatternMSK = '_';
// export const FFLNumberREGEX = /^\d{15}$/;
export const FFLNumberFULLREGEX = /^[a-zA-Z0-9]{15}$/;
export const FFLNumberLength = 15;
export const FFLNumberAllowedREGEX = /^[a-zA-Z0-9]*$/;
export const FFLNumberCleanREGEX = /[^a-zA-Z0-9]/g;
export const FFLNumberPlaceholders = ['X', 'XX', 'XXX', 'XX', 'XX', 'XXXXX'];
export const FFLNumberBoxLengths = [1, 2, 3, 2, 2, 5];

export const ipValidationRegExp =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export const passwordRules = () =>
  Yup.string()
    .required(t('required_error_msg', { field_name: t('password') }))
    // A chain of .min()/.max()/.matches() stops at the first one that fails, so a password
    // missing three things only ever reported the first - this checks every requirement and
    // reports all of them the user still needs to fix, in one message.
    .test('password-requirements', '', function (value) {
      if (!value) return true;

      const missing: string[] = [];
      if (value.length < 8) missing.push(t('password_min_length', { count: 8 }));
      if (value.length > 15) missing.push(t('password_max_length', { count: 15 }));
      if (!/[a-z]/.test(value)) missing.push(t('password_lowercase_required'));
      if (!/[A-Z]/.test(value)) missing.push(t('password_uppercase_required'));
      if (!/\d/.test(value)) missing.push(t('password_number_required'));
      if (!/[@$!%*?&#^()_\-+=]/.test(value)) missing.push(t('password_special_required'));
      if (/\s/.test(value)) missing.push(t('password_no_spaces'));

      if (missing.length === 0) return true;

      // Joined with \n rather than a space so MultilineFieldError can put each requirement on
      // its own line instead of running them together into one paragraph.
      return this.createError({ message: missing.join('\n') });
    });
export const FFLNumberRules = (fieldLabel = 'ffl') =>
  Yup.string()
    .transform((value) => value?.replace(FFLNumberCleanREGEX, '') || '')
    .required(t('required_error_msg', { field_name: t(fieldLabel) }))
    .matches(FFLNumberFULLREGEX, t('invalid_error_msg', { field_name: t(fieldLabel) }));
export const dateOfBirthMonthRules = (fieldLabel = 'date_of_birth') =>
  Yup.string()
    .required(t('required_error_msg', { field_name: t(fieldLabel) }))
    .matches(/^(0?[1-9]|1[0-2])$/, t('invalid_error_msg', { field_name: t(fieldLabel) }));

export const dateOfBirthDayRules = (fieldLabel = 'date_of_birth') =>
  Yup.string()
    .required(t('required_error_msg', { field_name: t(fieldLabel) }))
    .matches(/^(0?[1-9]|[12][0-9]|3[01])$/, t('invalid_error_msg', { field_name: t(fieldLabel) }));

export const dateOfBirthRules = (fieldLabel = 'date_of_birth') =>
  Yup.string()
    .required(t('required_error_msg', { field_name: t(fieldLabel) }))
    .length(4, t('invalid_error_msg', { field_name: t(fieldLabel) }))
    .test('valid-date', t('invalid_error_msg', { field_name: t(fieldLabel) }), function (year) {
      const { dob_month, dob_day } = this.parent;
      if (!dob_month || !dob_day || !year || year.length !== 4) return true;
      const dobDate = moment(
        `${year}-${dob_month.padStart(2, '0')}-${dob_day.padStart(2, '0')}`,
        'YYYY-MM-DD',
        true
      );
      return dobDate.isValid();
    })
    .test('min-age-18', t('must_be_at_least_18_years_old'), function (year) {
      const { dob_month, dob_day } = this.parent;
      if (!dob_month || !dob_day || !year || year.length !== 4) return true;
      const dobDate = moment(
        `${year}-${dob_month.padStart(2, '0')}-${dob_day.padStart(2, '0')}`,
        'YYYY-MM-DD',
        true
      );
      if (!dobDate.isValid()) return true;
      const age = moment().diff(dobDate, 'years', true);
      return age >= 18;
    });
